import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { User, ChevronRight, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Badge } from '@/components/ui/badge';
import MobileTooltip from '@/components/MobileTooltip';
import CountryFilter from '@/components/filters/CountryFilter';
import { sanitizeId } from '@/components/utils/id';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import PlaybookMarker from '@/components/PlaybookMarker';
import { PlaybookStatusProvider } from '@/components/PlaybookStatusContext';
import { useTranslation } from '@/components/utils/translations';
import PersonAvatar from '@/components/PersonAvatar';
import PursuitMarker from '@/components/PursuitMarker';
import { personaFitBand } from '@/lib/personaFitLabel';

const PERSONA_COLORS = {
  strategic: "bg-green-100 text-green-800",
  supportive: "bg-blue-100 text-blue-800",
  passive: "bg-gray-100 text-gray-800",
  lowFit: "bg-red-100 text-red-800",
};
const getPersonaLabel = (score, t) => {
  const band = personaFitBand(score) ?? 'passive';
  return { label: t(band), color: PERSONA_COLORS[band] };
};

export default function TopPersonasCard({ personas, country = "all", onCountryChange, countryOptions = [{ label: "All Countries", value: "all" }], currentTenant }) {
  const { t } = useTranslation();
  console.log('👤 TopPersonasCard received:', {
    personasLength: personas?.length || 0,
    country,
    sample: personas?.slice(0, 3).map(p => ({
      name: `${p.first_name} ${p.last_name}`,
      fit_score: p.persona_fit_score,
      tenant_id: p.tenant_id,
      country: p.country
    }))
  });

  const topPersonas = useMemo(() => {
    const preFiltered = (personas || [])
      .filter(p => p.is_visible !== false && p.persona_fit_score);
    
    const topCandidates = preFiltered
      .sort((a, b) => (b.persona_fit_score || 0) - (a.persona_fit_score || 0))
      .slice(0, 5);

    console.log('👤 TopPersonasCard filtered:', {
      before: personas?.length || 0,
      afterPreFilter: preFiltered.length,
      afterTopCandidateSort: topCandidates.length,
      countryFilter: country,
      topPersonas: topCandidates.map(p => ({
        name: `${p.first_name} ${p.last_name}`,
        score: p.persona_fit_score,
        country: p.country
      }))
    });

    return topCandidates;
  }, [personas, country]);

  const personIds = topPersonas.map(p => p.person_id || p.id).filter(Boolean);

  return (
    <PlaybookStatusProvider personIds={personIds} currentTenant={currentTenant}>
      <Card className="border-0 shadow-md">
      <CardHeader className="pb-2 md:pb-3">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-1">
            <User className="w-4 h-4 text-[#00C2C2] flex-shrink-0" />
            <CardTitle className="text-sm font-semibold text-[#0A1A2F]">{t('recommendedPersonas')}</CardTitle>
            <MobileTooltip 
              content={<p className="text-xs">{t('recommendedPersonasTooltip')}</p>}
              side="bottom"
              align="start"
              className="bg-[#0A1A2F] text-white border-0 shadow-lg"
            >
              <button className="p-0.5 rounded-full hover:bg-gray-100 transition-colors touch-manipulation">
                <Info className="w-3 h-3 text-[#5C6B7A]" />
              </button>
            </MobileTooltip>
          </div>
          
          {countryOptions.length > 1 && (
            <CountryFilter
              value={country}
              onChange={onCountryChange || (() => {})}
              options={countryOptions}
              className="w-auto min-w-[8rem] max-w-[11rem] h-8 text-xs gap-1"
            />
          )}
        </div>
        <Link to={createPageUrl('People')} className="text-xs font-medium text-[#00C2C2] hover:text-[#00A3A3]">
          {t('viewAll')}
        </Link>
      </CardHeader>
      <CardContent className="pt-0 px-3 md:px-6">
        <div className="space-y-2">
          {topPersonas.map((persona) => {
            const fit = getPersonaLabel(persona.persona_fit_score, t);
            const fullName = `${persona.first_name} ${persona.last_name}`;
            const personIdForLink = sanitizeId(persona.person_id || persona.id);
            if (!personIdForLink) return null;
            
            return (
              <Link
                key={persona.id || persona.person_id}
                to={createPageUrl(`PersonProfile?id=${encodeURIComponent(personIdForLink)}`)}
                className="block p-2 rounded-lg hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
                  <PersonAvatar
                      personId={persona.person_id || persona.id}
                      hostedPhotoUrl={persona.hosted_photo_url}
                      linkedinPhotoUrl={persona.profile_photo_url}
                      firstName={persona.first_name}
                      lastName={persona.last_name}
                      size="sm"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="font-medium text-sm text-[#0A1A2F] truncate group-hover:text-[#00C2C2] transition-colors">
                          {fullName}
                        </p>
                        <PlaybookMarker personId={persona.person_id || persona.id} />
                        <PursuitMarker isInPursuit={persona.add_to_pursuit} />
                      </div>
                      <p className="text-xs text-[#5C6B7A] truncate">{persona.role_title}</p>
                      <p className="text-xs text-[#5C6B7A] truncate">{persona.account_name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 md:gap-1.5 flex-shrink-0">
                    {persona.worked_for_customer && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button 
                            className="cursor-help flex-shrink-0"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            <Badge className="bg-blue-500 text-white text-[9px] h-5 px-1.5 whitespace-nowrap">
                              {t('formerClient')}
                            </Badge>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent 
                          side="left"
                          align="center"
                          className="bg-[#0A1A2F] text-white border-0 shadow-lg"
                        >
                          <p className="text-xs">{persona.client_name || t('previouslyWorked')}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {persona.persona_score_rationale ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button 
                            className="cursor-help flex-shrink-0"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          >
                            <Badge className={`${fit.color} text-[10px] h-5 whitespace-nowrap px-1.5`}>
                              {fit.label} ({persona.persona_fit_score})
                            </Badge>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent 
                          side="bottom" 
                          align="center"
                          className="w-[90vw] max-w-[400px] md:w-[400px] bg-[#0A1A2F] text-white border-0 shadow-lg p-4"
                        >
                          <p className="text-sm font-semibold mb-2 text-[#00C2C2]">{t('personaFitRationale')}</p>
                          <p className="text-sm text-white leading-relaxed">{persona.persona_score_rationale}</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <Badge className={`${fit.color} text-[10px] h-5 whitespace-nowrap px-1.5`}>
                        {fit.label} ({persona.persona_fit_score})
                      </Badge>
                    )}
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#00C2C2] transition-colors hidden sm:block" />
                  </div>
                </div>
              </Link>
            );
          })}
          {topPersonas.length === 0 && (
            <div className="text-center py-4 text-[#5C6B7A]">
              <User className="w-6 h-6 mx-auto mb-1 opacity-50" />
              <p className="text-xs">{t('noPersonas')}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
    </PlaybookStatusProvider>
  );
}
